import { GlossaryBlueprint } from "../../types-games";
import { generateUUID } from "./uuid";
import { AVAILABLE_FIELDS, blueprintTypeToFieldKey, FieldDefinition } from "./fieldDefinitions";
import { urlValue } from "@/lib/stores/objectsStore";

export interface FormField {
  id: string;
  type: keyof typeof AVAILABLE_FIELDS;
  label: string;
  value?: string | number | boolean | File | urlValue | string[];
  required?: boolean;
  placeholder?: string;
  options?: string[];
  accept?: string;
  defaultValue?: any;
  multiple?: boolean;
  filter?: boolean;
  filterOptions?: string[];
  translation?: boolean;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
}

export interface ParsedBlueprintData {
  fields: FormField[];
  initialData: Record<string, any>;
  metadata?: {
    title?: string;
    description?: string;
    version?: number;
  };
  sections?: Array<{
    id: string;
    title: string;
    fieldIds: string[];
  }>;
}

/**
 * Parse blueprint data to extract form fields and initialize form data
 * This utility ensures consistent parsing across glossary, blueprint editor, and document forms
 */
export function parseBlueprintData(blueprint: GlossaryBlueprint): ParsedBlueprintData {
  const fields: FormField[] = [];
  const sections: Array<{ id: string; title: string; fieldIds: string[] }> = [];
  // Parse fields from blueprint.data
  if (blueprint.data && typeof blueprint.data === 'object') {
    const dataFields = blueprint.data as Record<string, any>;
    // Handle glossary blueprint structure with sections

    if (dataFields.sections && typeof dataFields.sections === 'object') {
      Object.entries(dataFields.sections).forEach(([sectionKey, sectionConfig]) => {
        if (typeof sectionConfig === 'object') {
          const sectionFieldIds: string[] = [];
          const sectionTitle = (sectionConfig as any)._sectionTitle || sectionKey;

          // Check if this is a single field section (has direct type property)
          if ((sectionConfig as any).type) {
            // Single field section
            const fieldConfig = sectionConfig as any;
            const fieldId = fieldConfig.id || `${sectionKey}_field`;
            sectionFieldIds.push(fieldId);

            fields.push({
              id: fieldId,
              type: fieldConfig.type,
              label: fieldConfig.label || sectionTitle,
              required: fieldConfig.required || fieldConfig.req || false,
              placeholder: fieldConfig.placeholder || `Enter ${fieldConfig.label || sectionTitle}`,
              options: fieldConfig.options,
              accept: fieldConfig.accept,
              defaultValue: fieldConfig.defaultValue,
              multiple: fieldConfig.multiple || false,
              validation: fieldConfig.validation,
              translation: fieldConfig.translation || fieldConfig.trans    || false,  
              filter: fieldConfig.filter || false,
              filterOptions: fieldConfig.filterOptions || [],
            });
          } else {
            // Multi-field section (more than one field in the section)
            Object.entries(sectionConfig as Record<string, any>).forEach(([fieldKey, fieldConfig]) => {
              // Skip the _fieldOrder property
              if (fieldKey === '_fieldOrder') return;

              if (typeof fieldConfig === 'object' && fieldConfig.type) {
                const fieldId = fieldConfig.id || `${sectionKey}_${fieldKey}`;
                sectionFieldIds.push(fieldId);

                fields.push({
                  id: fieldId,
                  type: fieldConfig.type,
                  label: fieldConfig.label || fieldKey,
                  required: fieldConfig.required || fieldConfig.req || false,
                  placeholder: fieldConfig.placeholder || `Enter ${fieldConfig.label || fieldKey}`,
                  options: fieldConfig.options,
                  accept: fieldConfig.accept,
                  defaultValue: fieldConfig.defaultValue,
                  multiple: fieldConfig.multiple || false,
                  validation: fieldConfig.validation,
                  translation: fieldConfig.trans || false,
                  filter: fieldConfig.filter || false,
                  filterOptions: fieldConfig.filterOptions || [],
                });
              }
            });
          }
          // Add section info if it has fields
          if (sectionFieldIds.length > 0) {
            sections.push({
              id: sectionKey,
              title: sectionTitle,
              fieldIds: sectionFieldIds,
            });
          }
        }
      });
    }
  }

  // If no fields found in data, use default fields based on blueprint type
  if (fields.length === 0) {
    fields.push(...getDefaultFieldsForBlueprintType(blueprint.type || 'document'));
  }

  // Initialize form data with default values
  const initialData: Record<string, any> = {};
  fields.forEach(field => {
    initialData[field.id] = field.defaultValue !== undefined ? field.defaultValue : '';
  });

  // Extract metadata
  const metadata = {
    title: blueprint.name,
    description: blueprint.description,
    version: blueprint.version?.version,
  };

  return {
    fields,
    initialData,
    metadata,
    sections: sections.length > 0 ? sections : undefined,
  };
}

/**
 * Get default fields based on blueprint type
 */
function getDefaultFieldsForBlueprintType(type: string): FormField[] {
  switch (type.toLowerCase()) {
    case 'document':
      return [
        { id: 'title', type: 'title', label: AVAILABLE_FIELDS.title.label, required: AVAILABLE_FIELDS.title.required, placeholder: AVAILABLE_FIELDS.title.placeholder },
        { id: 'content', type: 'textarea', label: AVAILABLE_FIELDS.textarea.label, required: AVAILABLE_FIELDS.textarea.required, placeholder: AVAILABLE_FIELDS.textarea.placeholder },
        { id: 'file', type: 'file', label: AVAILABLE_FIELDS.file.label, required: AVAILABLE_FIELDS.file.required, accept: AVAILABLE_FIELDS.file.accept }
      ];

    case 'glossary':
      return [
        { id: 'term', type: 'title', label: 'Term', required: true, placeholder: 'Enter term' },
        { id: 'definition', type: 'textarea', label: 'Definition', required: true, placeholder: 'Enter definition' },
        { id: 'context', type: 'textarea', label: 'Context', placeholder: 'Enter context' },
        { id: 'category', type: 'select', label: 'Category', options: ['Character', 'Location', 'Item', 'Concept', 'Other'] }
      ];

    case 'character':
      return [
        { id: 'name', type: 'title', label: 'Character Name', required: true, placeholder: 'Enter character name' },
        { id: 'description', type: 'textarea', label: 'Description', required: true, placeholder: 'Enter character description' },
        { id: 'role', type: 'select', label: 'Role', options: ['Protagonist', 'Antagonist', 'Supporting', 'Minor'] },
        { id: 'image', type: 'file', label: 'Character Image', accept: AVAILABLE_FIELDS.file.accept }
      ];

    case 'location':
      return [
        { id: 'name', type: 'title', label: 'Location Name', required: true, placeholder: 'Enter location name' },
        { id: 'description', type: 'textarea', label: 'Description', required: true, placeholder: 'Enter location description' },
        { id: 'type', type: 'select', label: 'Location Type', options: ['City', 'Building', 'Landmark', 'Region', 'Other'] },
        { id: 'image', type: 'file', label: 'Location Image', accept: AVAILABLE_FIELDS.file.accept }
      ];

    default:
      return [
        { id: 'title', type: 'title', label: AVAILABLE_FIELDS.title.label, required: AVAILABLE_FIELDS.title.required, placeholder: AVAILABLE_FIELDS.title.placeholder },
        { id: 'description', type: 'textarea', label: AVAILABLE_FIELDS.textarea.label, required: AVAILABLE_FIELDS.textarea.required, placeholder: AVAILABLE_FIELDS.textarea.placeholder }
      ];
  }
}

/**
 * Validate form data against field requirements
 * @param data - Form data to validate
 * @param fields - Field definitions
 * @param allowDraft - If true, skip required field validation (for draft saves)
 */
export function validateFormData(
  data: Record<string, any>, 
  fields: FormField[], 
  allowDraft: boolean = false
): { isValid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  fields.forEach(field => {
    const value = data[field.id];

    // Check required fields (skip if saving as draft)
    if (!allowDraft && field.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      errors[field.id] = `${field.label} is required`;
      return;
    }

    // Skip validation for empty optional fields
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return;
    }

    // Validate based on field type (always validate format, even for drafts)
    if (field.validation) {
      if (field.type === 'number') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          errors[field.id] = `${field.label} must be a number`;
        } else if (field.validation.min !== undefined && numValue < field.validation.min) {
          errors[field.id] = `${field.label} must be at least ${field.validation.min}`;
        } else if (field.validation.max !== undefined && numValue > field.validation.max) {
          errors[field.id] = `${field.label} must be at most ${field.validation.max}`;
        }
      }

      if (field.validation.pattern && typeof value === 'string') {
        const regex = new RegExp(field.validation.pattern);
        if (!regex.test(value)) {
          errors[field.id] = field.validation.message || `${field.label} format is invalid`;
        }
      }
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Convert form data to blueprint data format
 */
export function formDataToBlueprintData(data: Record<string, any>, fields: FormField[]): Record<string, any> {
  const blueprintData: Record<string, any> = {};

  fields.forEach(field => {
    if (data[field.id] !== undefined) {
      blueprintData[field.id] = {
        type: field.type,
        label: field.label,
        required: field.required,
        placeholder: field.placeholder,
        options: field.options,
        accept: field.accept,
        defaultValue: data[field.id],
        validation: field.validation,
      };
    }
  });

  return blueprintData;
}

/**
 * Convert existing blueprint data to sections format for the editor
 * This helps when editing existing blueprints to show their current structure
 */
export function convertBlueprintDataToSections(blueprint: GlossaryBlueprint): {
  sections: Array<{
    id: string;
    title: string;
    fields: Array<{
      id: string;
      type: string;
      label: string;
      mandatory: boolean;
      translation: boolean;
    }>;
  }>;
  notes: Array<{
    id: string;
    color: string;
    note: string;
  }>;
  order: Array<{ type: 'section' | 'note'; id: string }>;
} {
  const sections: any[] = [];
  const notes: any[] = [];
  const order: Array<{ type: 'section' | 'note'; id: string }> = [];

  // Check if elementOrder exists and determine if it's ID-based or name-based
  let elementOrder: string[] = [];
  let isIdBasedOrder = false;

  if (blueprint.data && typeof blueprint.data === 'object') {
    const dataFields = blueprint.data as Record<string, any>;

    // Check if elementOrder exists and determine if it's ID-based or name-based
    elementOrder = dataFields.elementOrder || [];
    isIdBasedOrder = elementOrder.length > 0 && elementOrder.every((item: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item)
    );

    Object.entries(dataFields).forEach(([key, fieldConfig]) => {
      if (key.startsWith('DevNode_')) {
        // Developer note
        const noteId = key.replace('DevNode_', '');
        notes.push({
          id: noteId,
          color: fieldConfig.color || '#FF6B6B',
          note: fieldConfig.note || 'Developer note...',
        });
        order.push({ type: 'note', id: noteId });
      } else if (key === 'sections' && typeof fieldConfig === 'object') {
        // Direct sections object containing field objects
        Object.entries(fieldConfig).forEach(([sectionKey, sectionConfig]) => {
          if (typeof sectionConfig === 'object') {
            // Check if sectionKey is an ID (UUID format) or a title
            const isSectionId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sectionKey);
            const sectionId = isSectionId ? sectionKey : generateUUID();
            // Use _sectionTitle if available, otherwise fall back to sectionKey or title
            const sectionTitle = (sectionConfig as any)._sectionTitle ||
              (isSectionId ? (sectionConfig as any).title || 'Untitled Section' : sectionKey);
            const sectionDocumentation = (sectionConfig as any)._sectionDocumentation;
            const sectionFields: any[] = [];

            // Check if this is a single field section (has direct type property)
            if ((sectionConfig as any).type) {
              // Single field section
              const fieldConfig = sectionConfig as any;
              sectionFields.push({
                id: fieldConfig.id || generateUUID(),
                type: blueprintTypeToFieldKey(fieldConfig.type),
                label: fieldConfig.label || sectionKey,
                mandatory: fieldConfig.required || fieldConfig.req || false,
                translation: fieldConfig.translation || fieldConfig.trans || false,
                multiple: fieldConfig.multiple || false,
                filter: fieldConfig.filter || false,
                filterOptions: fieldConfig.filterOptions || [],
                documentation: fieldConfig.documentation,
                hiddenForRoles: fieldConfig.hiddenForRoles || [], // Add role visibility
              });
            } else {
              // Multi-field section
              Object.entries(sectionConfig as Record<string, any>).forEach(([fieldKey, fieldConfig]) => {
                // Skip the _fieldOrder property
                if (fieldKey === '_fieldOrder') return;

                if (typeof fieldConfig === 'object' && fieldConfig.type) {
                  // Check if fieldKey is an ID (UUID format) or a label
                  const isFieldId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fieldKey);
                  const fieldId = isFieldId ? fieldKey : (fieldConfig.id || generateUUID());
                  const fieldLabel = isFieldId ? (fieldConfig.label || 'Untitled Field') : fieldKey;

                  sectionFields.push({
                    id: fieldId,
                    type: blueprintTypeToFieldKey(fieldConfig.type),
                    label: fieldLabel,
                    mandatory: fieldConfig.required || fieldConfig.req || false,
                    translation: fieldConfig.translation || fieldConfig.trans || false,
                    multiple: fieldConfig.multiple || false,
                    hiddenForRoles: fieldConfig.hiddenForRoles || [], // Add role visibility
                    filter: fieldConfig.filter || false,
                    filterOptions: fieldConfig.filterOptions || [], // Add this line
                    documentation: fieldConfig.documentation, // Add field documentation
                  });
                } else if (typeof fieldConfig === 'object' && fieldConfig.id && fieldConfig.type && fieldConfig.type !== 'glossaryItem') {
                  // Handle case where the field config is directly in the section
                  sectionFields.push({
                    id: fieldConfig.id,
                    type: blueprintTypeToFieldKey(fieldConfig.type),
                    label: fieldConfig.label || fieldKey,
                    mandatory: fieldConfig.required || fieldConfig.req || false,
                    translation: fieldConfig.translation || fieldConfig.trans || false,
                    multiple: fieldConfig.multiple || false,
                    filter: fieldConfig.filter || false,
                    filterOptions: fieldConfig.filterOptions || [],
                    documentation: fieldConfig.documentation,
                    hiddenForRoles: fieldConfig.hiddenForRoles || [], // Add role visibility
                  });
                }
              });
            }

            if (sectionFields.length > 0) {
              // Check if there's a field order stored in the data
              const fieldOrder = (sectionConfig as any)._fieldOrder;

              // Sort fields according to the stored order
              if (fieldOrder && Array.isArray(fieldOrder)) {
                sectionFields.sort((a, b) => {
                  // Check if fieldOrder contains IDs or labels
                  const isIdBased = fieldOrder.every((item: string) =>
                    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item)
                  );

                  if (isIdBased) {
                    // Sort by ID
                    const aIndex = fieldOrder.indexOf(a.id);
                    const bIndex = fieldOrder.indexOf(b.id);
                    return aIndex - bIndex;
                  } else {
                    // Sort by label (backward compatibility)
                    const aIndex = fieldOrder.indexOf(a.label);
                    const bIndex = fieldOrder.indexOf(b.label);
                    return aIndex - bIndex;
                  }
                });
              }

              sections.push({
                id: sectionId,
                title: sectionTitle,
                fields: sectionFields,
                fieldOrder: fieldOrder || sectionFields.map(f => f.id),
                documentation: sectionDocumentation,
                hiddenForRoles: (sectionConfig as any).hiddenForRoles || [], // Add section role visibility
              });
              order.push({ type: 'section', id: sectionId });
            }
          }
        });
      } else if (typeof fieldConfig === 'object' && fieldConfig.type) {
        // Single field section (this case handles when a section has a direct type property)
        const sectionId = generateUUID();
        const fieldId = generateUUID();
        // Use _sectionTitle if available, otherwise use key
        const sectionTitle = (fieldConfig as any)._sectionTitle || key;
        sections.push({
          id: sectionId,
          title: sectionTitle,
          fields: [{
            id: fieldId,
            type: blueprintTypeToFieldKey(fieldConfig.type),
            label: fieldConfig.label || key,
            mandatory: fieldConfig.required || fieldConfig.req || false,
            translation: fieldConfig.translation || fieldConfig.trans || false,
            multiple: fieldConfig.multiple || false,
            filter: fieldConfig.filter || false,
          }],
          fieldOrder: [fieldId],
        });
        order.push({ type: 'section', id: sectionId });
      } else if (typeof fieldConfig === 'object' && fieldConfig.sections) {
        // Multiple fields section (nested structure)
        Object.entries(fieldConfig.sections).forEach(([sectionKey, sectionConfig]) => {
          if (typeof sectionConfig === 'object') {
            const sectionId = generateUUID();
            const sectionFields: any[] = [];

            Object.entries(sectionConfig as Record<string, any>).forEach(([fieldKey, fieldConfig]) => {
              if (typeof fieldConfig === 'object' && fieldConfig.type) {
                sectionFields.push({
                  id: generateUUID(),
                  type: fieldConfig.type,
                  label: fieldConfig.label || fieldKey,
                  mandatory: fieldConfig.required || fieldConfig.req || false,
                  translation: fieldConfig.translation || fieldConfig.trans || false,
                  multiple: fieldConfig.multiple || false,
                  filter: fieldConfig.filter || false,
                  filterOptions: fieldConfig.filterOptions || [], // Add this line
                });
              }
            });

            if (sectionFields.length > 0) {
              // Check if there's a field order stored in the data
              const fieldOrder = (sectionConfig as any)._fieldOrder;

              // Sort fields according to the stored order
              if (fieldOrder && Array.isArray(fieldOrder)) {
                sectionFields.sort((a, b) => {
                  const aIndex = fieldOrder.indexOf(a.label);
                  const bIndex = fieldOrder.indexOf(b.label);
                  return aIndex - bIndex;
                });
              }

              // Use _sectionTitle if available, otherwise use sectionKey
              const sectionTitle = (sectionConfig as any)._sectionTitle || sectionKey;

              sections.push({
                id: sectionId,
                title: sectionTitle,
                fields: sectionFields,
                fieldOrder: fieldOrder || sectionFields.map(f => f.id), // Use stored order or current order
              });
              order.push({ type: 'section', id: sectionId });
            }
          }
        });
      }
    });
  }

  // If no sections found, create default section
  if (sections.length === 0) {
    const defaultSectionId = generateUUID();
    const defaultFieldType = Object.keys(AVAILABLE_FIELDS)[0] as keyof typeof AVAILABLE_FIELDS;
    const defaultFieldDef = AVAILABLE_FIELDS[defaultFieldType];

    sections.push({
      id: defaultSectionId,
      title: 'New Section',
      fields: [{
        id: generateUUID(),
        type: defaultFieldType,
        label: defaultFieldDef.label,
        mandatory: defaultFieldDef.required || false,
        translation: false,
        multiple: false,
        filter: false,
      }],
    });
    order.push({ type: 'section', id: defaultSectionId });
  }

  // If elementOrder exists, use it to sort the order array
  if (elementOrder.length > 0) {

    // Create a map of existing items by their ID or title
    const sectionsMap = new Map(sections.map(s => [isIdBasedOrder ? s.id : s.title, s]));
    const notesMap = new Map(notes.map(n => [isIdBasedOrder ? n.id : `DevNode_${n.id}`, n]));

    // Clear the order array and rebuild it based on elementOrder
    order.length = 0;

    elementOrder.forEach(elementId => {
      if (sectionsMap.has(elementId)) {
        const section = sectionsMap.get(elementId)!;
        order.push({ type: 'section', id: section.id });
      } else if (notesMap.has(elementId)) {
        const note = notesMap.get(elementId)!;
        order.push({ type: 'note', id: note.id });
      } else {
      }
    });
  }

  

  return { sections, notes, order };
} 